import { useState, useEffect, useRef } from 'react';
import {
    MessageSquare, Layout, Settings,
    Search, Phone, Video, MoreVertical, CheckCircle,
    Plus, ArrowRight, Bot, Paperclip, Moon, Sun, QrCode
} from 'lucide-react';

// --- Types ---

type Theme = 'light' | 'dark' | 'system';
type ContactStatus = 'Novo Lead' | 'Em Negociação' | 'Aguardando Pagamento' | 'Fechado' | 'Perdido';

type Message = {
    id: string;
    text: string;
    sender: 'me' | 'them';
    timestamp: string;
    type: 'text' | 'audio' | 'image' | 'system';
    status: 'sent' | 'delivered' | 'read';
    isDeleted?: boolean;
};

type Note = {
    id: string;
    content: string;
    date: string;
};

type Contact = {
    id: string;
    name: string;
    phone: string;
    avatar: string;
    lastMessage: string;
    unread: number;
    status: ContactStatus;
    tags: string[];
    notes: Note[];
    messages: Message[];
    email?: string;
    company?: string;
};

// --- Mock Data ---

const INITIAL_CONTACTS: Contact[] = [
    {
        id: '1',
        name: 'Ana Silva',
        phone: '+55 11 99999-1111',
        avatar: 'https://i.pravatar.cc/150?u=1',
        lastMessage: 'Gostaria de saber mais sobre o plano.',
        unread: 2,
        status: 'Novo Lead',
        tags: ['Interessado'],
        notes: [{ id: 'n1', content: 'Cliente veio pelo Instagram', date: '2023-10-25' }],
        messages: [
            { id: 'm1', text: 'Olá, boa tarde!', sender: 'them', timestamp: '14:30', type: 'text', status: 'read' },
            { id: 'm2', text: 'Gostaria de saber mais sobre o plano.', sender: 'them', timestamp: '14:31', type: 'text', status: 'read' }
        ]
    },
    {
        id: '2',
        name: 'Carlos Oliveira',
        phone: '+55 11 98888-2222',
        avatar: 'https://i.pravatar.cc/150?u=2',
        lastMessage: 'Vou verificar com meu sócio.',
        unread: 0,
        status: 'Em Negociação',
        tags: ['B2B', 'Quente'],
        notes: [],
        messages: [
            { id: 'm1', text: 'Preço enviado.', sender: 'me', timestamp: '10:00', type: 'text', status: 'read' },
            { id: 'm2', text: 'Vou verificar com meu sócio.', sender: 'them', timestamp: '10:15', type: 'text', status: 'read' }
        ]
    },
];

const KANBAN_COLUMNS: ContactStatus[] = ['Novo Lead', 'Em Negociação', 'Aguardando Pagamento', 'Fechado', 'Perdido'];

// --- Components ---

export default function App() {
    const [activeTab, setActiveTab] = useState<'chat' | 'kanban' | 'settings'>('chat');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [contacts, setContacts] = useState<Contact[]>(INITIAL_CONTACTS);
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
    const [messageInput, setMessageInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [theme, setTheme] = useState<Theme>('light');

    // Theme Logic
    const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    useEffect(() => {
        if (isDark) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDark]);

    const selectedContact = contacts.find(c => c.id === selectedContactId);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [selectedContact?.messages]);

    // --- Handlers ---

    const handleSendMessage = () => {
        if (!messageInput.trim() || !selectedContactId) return;
        const newMessage: Message = {
            id: Date.now().toString(),
            text: messageInput,
            sender: 'me',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            type: 'text',
            status: 'sent'
        };
        setContacts(prev => prev.map(c => c.id === selectedContactId ? { ...c, messages: [...c.messages, newMessage], lastMessage: messageInput } : c));
        setMessageInput('');
    };

    const simulateConnection = () => {
        setTimeout(() => { setIsConnected(true); }, 2000);
    };

    // --- Styles ---
    const bgMain = isDark ? 'bg-[#121212]' : 'bg-[#FAFAFA]';
    const bgSurface = isDark ? 'bg-[#1E1E1E]' : 'bg-white'; // Cards/Surfaces
    const bgSidebar = isDark ? 'bg-[#181818]' : 'bg-[#F4F4F5]'; // Sidebar
    const bgHover = isDark ? 'hover:bg-[#2A2A2A]' : 'hover:bg-[#EAEAEA]';

    const textPrimary = isDark ? 'text-[#EDEDED]' : 'text-[#18181B]';
    const textSecondary = isDark ? 'text-[#A1A1AA]' : 'text-[#71717A]';

    const border = isDark ? 'border-[#27272A]' : 'border-[#E4E4E7]';
    const borderHighlight = isDark ? 'border-[#3F3F46]' : 'border-[#D4D4D8]';

    const shadowSm = isDark ? 'shadow-none' : 'shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]';
    const shadowMd = isDark ? 'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.3)]' : 'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05),0_2px_4px_-1px_rgba(0,0,0,0.03)]';

    const renderSidebar = () => (
        <div className={`w-80 flex-shrink-0 border-r ${border} ${bgSidebar} flex flex-col z-10 sm:w-full md:w-80`}>
            {/* Header */}
            <div className={`p-5 flex justify-between items-center`}>
                <div className={`flex items-center gap-3 font-semibold ${textPrimary}`}>
                    <div className={`w-6 h-6 rounded bg-gradient-to-br from-gray-800 to-black dark:from-white dark:to-gray-300 flex items-center justify-center text-white dark:text-black text-xs shadow-md`}>W</div>
                    <span className="tracking-tight">Workspace</span>
                </div>
                <button className={`${textSecondary} hover:${textPrimary} transition-colors bg-white dark:bg-[#2C2C2C] p-1.5 rounded-md ${shadowSm} border ${border}`}>
                    <Plus size={16} />
                </button>
            </div>

            {/* Search */}
            <div className="px-4 pb-4">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-[#27272A]' : 'bg-white'} border ${border} ${textSecondary} focus-within:ring-2 focus-within:ring-black/5 transition-all shadow-sm`}>
                    <Search size={14} />
                    <input
                        type="text"
                        placeholder="Pesquisar chats..."
                        className="bg-transparent border-none outline-none text-sm w-full placeholder-inherit font-medium"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Navigation List */}
            <div className="flex-1 overflow-y-auto px-3 space-y-1">
                <div className={`px-2 py-2 text-[11px] font-bold ${textSecondary} uppercase tracking-wider opacity-80`}>Caixa de Entrada</div>
                {contacts.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase())).map(contact => {
                    const isActive = selectedContactId === contact.id;
                    return (
                        <div
                            key={contact.id}
                            onClick={() => { setSelectedContactId(contact.id); setActiveTab('chat'); }}
                            className={`
                group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 relative
                ${isActive
                                    ? `${isDark ? 'bg-[#2C2C2C]' : 'bg-white'} ${shadowMd} ring-1 ring-black/5`
                                    : `hover:bg-gray-200/50 dark:hover:bg-[#252525] border border-transparent`}
              `}
                        >
                            <div className="relative">
                                <img src={contact.avatar} className={`w-10 h-10 rounded-full object-cover shadow-sm ${isActive ? 'ring-2 ring-offset-2 ring-gray-100 dark:ring-offset-gray-900 dark:ring-gray-700' : ''}`} alt={contact.name} />
                                {contact.unread > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 rounded-full border-2 border-white dark:border-[#181818]"></span>}
                            </div>

                            <div className="flex-1 overflow-hidden">
                                <div className={`flex justify-between items-center mb-0.5`}>
                                    <span className={`truncate font-semibold text-sm ${isActive ? textPrimary : 'text-gray-700 dark:text-gray-300'}`}>{contact.name}</span>
                                    <span className={`text-[10px] ${textSecondary}`}>{contact.messages[contact.messages.length - 1]?.timestamp}</span>
                                </div>
                                <div className={`truncate text-xs ${isActive ? textSecondary : 'text-gray-500'} font-medium`}>
                                    {contact.lastMessage}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Mini Profile Bottom */}
            <div className={`p-4 border-t ${border} bg-opacity-50`}>
                <div className={`flex items-center gap-3 p-2 rounded-lg ${bgHover} cursor-pointer transition-colors`}>
                    <div className="w-8 h-8 bg-gradient-to-tr from-emerald-500 to-teal-400 rounded-full shadow-sm"></div>
                    <div className="flex-1 min-w-0">
                        <div className={`text-sm ${textPrimary} font-semibold truncate`}>Admin User</div>
                        <div className={`text-xs ${textSecondary} truncate`}>Pro Plan</div>
                    </div>
                    <Settings size={16} className={textSecondary} />
                </div>
            </div>
        </div>
    );

    const renderChat = () => {
        if (!selectedContact) return (
            <div className={`flex-1 flex flex-col items-center justify-center ${bgMain} ${textSecondary} hidden md:flex`}>
                <div className={`w-24 h-24 rounded-2xl ${bgSurface} border ${border} shadow-lg flex items-center justify-center mb-6 rotate-3`}>
                    <MessageSquare size={40} className="opacity-20" />
                </div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>Nenhum chat selecionado</h3>
                <p className="max-w-xs text-center text-sm">Selecione um contato na barra lateral ou inicie uma nova conversa.</p>
            </div>
        );

        return (
            <div className={`flex-1 flex flex-col h-full ${bgMain} absolute inset-0 z-20 md:static`}>
                <div className={`md:hidden h-14 flex items-center px-4 border-b ${border} ${bgSurface}`}>
                    <button onClick={() => setSelectedContactId(null)} className={`mr-3 ${textPrimary}`}>← Voltar</button>
                </div>

                {/* Chat Top Bar */}
                <div className={`h-16 border-b ${border} flex items-center justify-between px-6 bg-opacity-80 backdrop-blur-md sticky top-0 z-10`}>
                    <div className="flex items-center gap-4">
                        <img src={selectedContact.avatar} className="w-8 h-8 rounded-full md:hidden" alt={selectedContact.name} />
                        <div>
                            <h2 className={`font-bold text-base ${textPrimary}`}>{selectedContact.name}</h2>
                            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${border} ${bgSurface} shadow-sm w-fit mt-1`}>
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                                <span className={`text-xs font-medium ${textPrimary}`}>{selectedContact.status}</span>
                            </div>
                        </div>
                    </div>
                    <div className={`flex items-center gap-1`}>
                        <button className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2C2C2C] ${textSecondary} transition-colors`}><Phone size={18} /></button>
                        <button className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2C2C2C] ${textSecondary} transition-colors`}><Video size={18} /></button>
                        <div className={`w-px h-6 ${isDark ? 'bg-gray-800' : 'bg-gray-200'} mx-2`}></div>
                        <button className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2C2C2C] ${textSecondary} transition-colors`}><MoreVertical size={18} /></button>
                    </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8">
                    {selectedContact.messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'} group`}>
                            <div className={`max-w-[85%] md:max-w-[60%] flex gap-4 ${msg.sender === 'me' ? 'flex-row-reverse' : ''}`}>
                                {msg.sender === 'them' && (
                                    <img src={selectedContact.avatar} className="w-8 h-8 rounded-full shadow-sm mt-auto mb-1" alt="Avatar" />
                                )}

                                <div className={`flex flex-col ${msg.sender === 'me' ? 'items-end' : 'items-start'}`}>
                                    <div className={`
                      py-2.5 px-4 rounded-2xl text-[15px] leading-relaxed shadow-sm relative
                      ${msg.sender === 'me'
                                            ? 'bg-black text-white dark:bg-white dark:text-black rounded-br-none'
                                            : `${bgSurface} border ${border} ${textPrimary} rounded-bl-none`}
                    `}>
                                        {msg.text}
                                    </div>
                                    <span className={`text-[10px] ${textSecondary} mt-1.5 px-1 opacity-0 group-hover:opacity-100 transition-opacity`}>
                                        {msg.timestamp} • {msg.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className={`p-6 ${bgMain}`}>
                    <div className={`flex items-center gap-2 p-3 rounded-xl border ${borderHighlight} ${bgSurface} ${shadowMd} focus-within:ring-1 focus-within:ring-black/10 transition-all`}>
                        <button className={`${textSecondary} hover:${textPrimary} p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#2C2C2C]`}><Paperclip size={20} /></button>
                        <input
                            className={`flex-1 bg-transparent border-none outline-none text-sm ${textPrimary} placeholder:${textSecondary}`}
                            placeholder="Escreva sua mensagem..."
                            value={messageInput}
                            onChange={(e) => setMessageInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                        />
                        <div className="flex gap-1">
                            <button className={`${textSecondary} hover:text-purple-600 p-2 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20`}><Bot size={20} /></button>
                            <button onClick={handleSendMessage} className={`bg-black dark:bg-white text-white dark:text-black p-2 rounded-lg hover:opacity-90 transition-opacity`}><ArrowRight size={20} /></button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderCRMDetails = () => {
        if (!selectedContact) return null;

        return (
            <div className={`w-80 border-l ${border} ${bgSurface} hidden xl:flex flex-col shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.02)] z-10`}>
                <div className="p-6">
                    <div className="flex flex-col items-center mb-8">
                        <div className="relative">
                            <img src={selectedContact.avatar} className="w-20 h-20 rounded-2xl shadow-md mb-4 object-cover" alt={selectedContact.name} />
                            <div className="absolute -bottom-2 -right-2 bg-white dark:bg-black p-1.5 rounded-full shadow-sm border border-gray-100 dark:border-gray-800">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            </div>
                        </div>
                        <h3 className={`text-lg font-bold ${textPrimary}`}>{selectedContact.name}</h3>
                        <p className={`text-sm ${textSecondary}`}>{selectedContact.phone}</p>
                    </div>

                    <h3 className={`text-xs font-bold ${textSecondary} uppercase tracking-wider mb-4 pl-1`}>Pipeline</h3>
                    <div className={`p-1 rounded-xl bg-gray-50 dark:bg-[#151515] border ${border} mb-8`}>
                        {KANBAN_COLUMNS.map((s) => (
                            <div
                                key={s}
                                className={`
                   px-3 py-2 text-xs font-medium rounded-lg cursor-pointer flex items-center justify-between transition-all
                   ${selectedContact.status === s
                                        ? 'bg-white dark:bg-[#2C2C2C] shadow-sm text-black dark:text-white'
                                        : 'text-gray-400 hover:text-gray-600'}
                   `}
                            >
                                {s}
                                {selectedContact.status === s && <CheckCircle size={12} className="text-green-500" />}
                            </div>
                        ))}
                    </div>

                    <div className={`h-px ${bgSidebar} mb-6`}></div>

                    <h3 className={`text-xs font-bold ${textSecondary} uppercase tracking-wider mb-4 pl-1`}>Informações</h3>
                    <div className="space-y-4 px-1">
                        <div className="group">
                            <label className={`text-[10px] font-semibold ${textSecondary} mb-1 block`}>EMAIL</label>
                            <div className={`text-sm ${textPrimary} pb-1 border-b ${border} group-hover:border-gray-400 transition-colors`}>{selectedContact.email || "adicionar email"}</div>
                        </div>
                        <div className="group">
                            <label className={`text-[10px] font-semibold ${textSecondary} mb-1 block`}>EMPRESA</label>
                            <div className={`text-sm ${textPrimary} pb-1 border-b ${border} group-hover:border-gray-400 transition-colors`}>{selectedContact.company || "adicionar empresa"}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderKanban = () => (
        <div className={`flex-1 overflow-x-auto p-8 ${bgMain}`}>
            <div className="flex justify-between items-center mb-10 sticky left-0">
                <div>
                    <h1 className={`text-3xl font-bold ${textPrimary} tracking-tight`}>Vendas</h1>
                    <p className={`text-sm ${textSecondary} mt-1`}>Gerencie seu pipeline e acompanhe o progresso.</p>
                </div>
                <div className="flex gap-3">
                    <button className={`px-4 py-2 text-sm font-medium rounded-lg border ${border} ${bgSurface} ${textPrimary} ${shadowSm} hover:bg-gray-50 transition-all`}>Filtros</button>
                    <button className={`px-4 py-2 text-sm font-medium rounded-lg bg-black text-white dark:bg-white dark:text-black shadow-lg hover:opacity-90 transition-opacity`}>Novo Lead</button>
                </div>
            </div>

            <div className="flex gap-6 h-[calc(100%-100px)] min-w-max pb-4">
                {KANBAN_COLUMNS.map(col => (
                    <div key={col} className="w-72 flex flex-col group">
                        <div className="flex justify-between items-center mb-4 px-1">
                            <span className={`text-xs font-bold ${textSecondary} uppercase tracking-wider`}>{col}</span>
                            <span className={`text-[10px] font-bold ${textSecondary} bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full`}>
                                {contacts.filter(c => c.status === col).length}
                            </span>
                        </div>

                        <div className={`flex-1 space-y-3 p-1.5 rounded-xl transition-colors ${isDark ? 'group-hover:bg-[#181818]' : 'group-hover:bg-gray-50'}`}>
                            {contacts.filter(c => c.status === col).map(contact => (
                                <div
                                    key={contact.id}
                                    onClick={() => { setSelectedContactId(contact.id); setActiveTab('chat'); }}
                                    className={`
                        p-4 rounded-xl border ${border} ${bgSurface} ${shadowSm} 
                        cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-200
                      `}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <img src={contact.avatar} className="w-8 h-8 rounded-full" alt={contact.name} />
                                        <div className={`font-semibold text-sm ${textPrimary}`}>{contact.name}</div>
                                    </div>

                                    <div className={`text-xs ${textSecondary} line-clamp-2 mb-3 leading-relaxed`}>{contact.lastMessage}</div>

                                    <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-3">
                                        <div className="flex gap-1">
                                            {contact.tags.map(tag => (
                                                <span key={tag} className={`text-[9px] font-bold px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 ${textSecondary}`}>{tag}</span>
                                            ))}
                                        </div>
                                        <span className={`text-[10px] ${textSecondary}`}>2d</span>
                                    </div>
                                </div>
                            ))}

                            <button className={`
                    w-full py-3 text-xs font-medium border border-dashed ${border} rounded-xl ${textSecondary} 
                    hover:border-gray-400 hover:text-gray-600 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100
                 `}>
                                <Plus size={14} /> Adicionar
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // --- Main Layout ---

    return (
        <div className={`flex h-screen w-full font-sans antialiased selection:bg-black selection:text-white dark:selection:bg-white dark:selection:text-black ${bgMain}`}>
            {/* Rail Nav (Far Left) */}
            <div className={`w-16 flex-shrink-0 border-r ${border} ${bgSurface} flex flex-col items-center py-6 gap-6 z-30 hidden md:flex`}>
                <div className="mb-2">
                    <div className="w-8 h-8 bg-black dark:bg-white rounded-lg flex items-center justify-center">
                        <div className="w-3 h-3 bg-white dark:bg-black rounded-full"></div>
                    </div>
                </div>

                {[
                    { id: 'chat', icon: MessageSquare },
                    { id: 'kanban', icon: Layout },
                    { id: 'settings', icon: Settings },
                ].map((item) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const isActive = activeTab === item.id;
                    return (
                        <button
                            key={item.id}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onClick={() => setActiveTab(item.id as any)}
                            className={`
                 p-3 rounded-xl transition-all duration-300 relative group
                 ${isActive
                                    ? 'bg-black text-white dark:bg-white dark:text-black shadow-lg'
                                    : `${textSecondary} hover:bg-gray-100 dark:hover:bg-[#2C2C2C]`}
               `}
                        >
                            <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                            {/* Tooltip hint could go here */}
                            {!isActive && <div className="absolute left-full ml-4 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
                                {item.id.charAt(0).toUpperCase() + item.id.slice(1)}
                            </div>}
                        </button>
                    )
                })}
            </div>

            {/* Mobile Bottom Nav */}
            <div className={`md:hidden fixed bottom-0 w-full h-16 ${bgSurface} border-t ${border} flex justify-around items-center z-40`}>
                {[
                    { id: 'chat', icon: MessageSquare },
                    { id: 'kanban', icon: Layout },
                    { id: 'settings', icon: Settings },
                ].map((item) => {
                    const isActive = activeTab === item.id;
                    return (
                        <button
                            key={item.id}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onClick={() => setActiveTab(item.id as any)}
                            className={`p-3 ${isActive ? textPrimary : textSecondary}`}
                        >
                            <item.icon size={24} />
                        </button>
                    )
                })}
            </div>


            {activeTab === 'chat' && (
                <>
                    {renderSidebar()}
                    {renderChat()}
                    {renderCRMDetails()}
                </>
            )}

            {activeTab === 'kanban' && renderKanban()}

            {activeTab === 'settings' && (
                <div className={`flex-1 flex items-center justify-center ${bgMain} flex-col gap-6`}>
                    <div className={`p-8 rounded-2xl border ${border} ${bgSurface} shadow-xl max-w-md w-full text-center`}>
                        <QrCode size={48} className={`mx-auto mb-6 ${textPrimary}`} />
                        <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>Conectar WhatsApp</h2>
                        <p className={`text-sm ${textSecondary} mb-8`}>Escaneie o código para sincronizar suas mensagens.</p>

                        <div className="flex justify-center gap-4 mb-8">
                            <button onClick={() => setTheme('light')} className={`p-3 rounded-lg border ${border} hover:scale-105 transition-transform ${theme === 'light' ? 'bg-black text-white' : ''}`}><Sun size={20} /></button>
                            <button onClick={() => setTheme('dark')} className={`p-3 rounded-lg border ${border} hover:scale-105 transition-transform ${theme === 'dark' ? 'bg-white text-black' : ''}`}><Moon size={20} /></button>
                        </div>

                        <button
                            onClick={() => simulateConnection()}
                            className={`w-full py-3 rounded-lg bg-black text-white dark:bg-white dark:text-black font-medium shadow-lg hover:opacity-90 transition-opacity`}
                        >
                            {isConnected ? 'Sincronizado' : 'Gerar QR Code'}
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
}
